/* eslint-disable @typescript-eslint/explicit-function-return-type */
import styled, { css } from 'styled-components/macro';

import modularScale from 'styles/helpers/modular-scale';
import { definitionLabel } from 'styles/base-classes';

/**
 * Imported to apply custom focus- and hover-styles in child elements
 * Interaction happens on parent component
 * Parent component can be used as a selector e.g. ${ItemWrapper}:hover &
 */
import { ItemWrapper } from 'components/base/Menu/Menu';
import { ThemeProps } from 'types/styled';

export const SortingControls = styled.div<ThemeProps<'div'>>`
  display: flex;
  align-items: center;
  justify-content: ${({ flex, alignCenter }) =>
    alignCenter ? 'center' : `flex-${flex}`};
  ${({ redesignedTable }) => redesignedTable && 'width: max-content;'}
  ${({ isMinWidth }) => isMinWidth && 'min-width: 100px;'}
  margin: ${({ margin }) => margin};

  ${({ isActive, theme }) => css`
    ${HeaderContent}, .chevron {
      color: ${isActive ? theme.NightBlue : theme.CasperGrey};
      font-weight: ${isActive && 600};
    }
  `}

  ${({ tableName }) =>
    tableName === 'trackCustomLists' &&
    css`
      width: auto;
    `}

  [data-menu-figure] {
    right: ${({ isFirstOfTracksIamWatching }) =>
      isFirstOfTracksIamWatching ? '-60px' : '0'};
  }
`;

export const Header = styled.th<ThemeProps<'th'>>`
  font-weight: normal;
  line-height: 0.7;
  padding: ${({ theme, tableName }) =>
    tableName === 'BrowseOpus' || tableName === 'TopLists'
      ? '0px'
      : `${theme.spacing.small} ${theme.spacing.xsmall} ${theme.spacing.xsmall}`};

  &:first-of-type {
    padding-left: 0;
    span {
      width: 100%;
    }
    margin-right: auto;
  }

  ${({ tableName, headerName }) =>
    tableName === 'StreamingTerritories' &&
    css`
      ${headerName === 'countryName' &&
      `
        ${SortingControls} {
          justify-content: flex-start !important;
        }
      `}
      &:not(:first-of-type) {
        ${SortingControls} {
          width: 100%;
          justify-content: flex-end;
        }
      }
    `}

  &:last-of-type {
    padding-right: ${({ tableName }) => (tableName === 'chart' ? '1em' : '0')};
  }
`;

export const HeaderContent = styled.span<ThemeProps<'span'>>`
  display: ${(p) => p.hideVisually && 'none'};
  font-size: ${modularScale(-2)};
  text-transform: uppercase;
  letter-spacing: 0.1em;

  line-height: ${({ tableName }) =>
    tableName === 'BrowseOpus' ? 'normal' : '1.6'};
  white-space: ${({ multiline }) => (multiline ? 'pre-line' : 'nowrap')};
  width: ${({ tableName }) =>
    tableName === 'BrowseOpus' ||
    tableName === 'TopLists' ||
    tableName === 'StreamingTerritories'
      ? '6.5em'
      : '100%'};

  ${({ tableName, twtdOfGlobal }) =>
    (tableName === 'BrowseOpus' ||
      tableName === 'TopLists' ||
      twtdOfGlobal ||
      tableName === 'StreamingTerritories') &&
    css`
      display: block;
      text-align: center;
      white-space: pre-line;
    `}

  ${({ tableName }) =>
    tableName === 'trackCustomLists' &&
    css`
      display: block;
      text-align: center;
      white-space: pre;
    `}

  ${({ theme, isActive }) => css`
    color: ${isActive ? theme.NightBlue : theme.CasperGrey};
    font-weight: ${isActive ? 600 : 400};
  `}
`;

export const HeaderLabel = styled.div<{
  textAlign?: string;
  tableName?: string;
}>`
  text-align: ${({ textAlign = 'left' }): string => textAlign};
  white-space: ${({ tableName }) =>
    tableName === 'topCharts' ? 'initial' : 'pre'};
`;

export const HeaderButton = styled.button<ThemeProps<'span'>>`
  text-align: ${({ multiline }) => (multiline ? 'left' : 'right')};
  display: ${(p) => p.hideVisually && 'none'};
  margin-right: ${({ tableName }) =>
    tableName === 'concerts' && 'initial !important'};

  ${({ tableName }) =>
    (tableName === 'BrowseOpus' || tableName === 'TopLists') &&
    css`
      padding: 0;
    `}
  ${({ tableName }) =>
    tableName === 'trackCustomLists' &&
    css`
      text-align: center;
    `}

  &:hover,
  &:focus {
    background: ${(p) => p.theme.Sand};
    cursor: pointer;
  }
  &:active {
    background: ${(p) => p.theme.VeryLightGrey};
  }
`;

export const ButtonContent = styled.div<ThemeProps<'span'>>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${(p) => (p.isOpen ? p.theme.LightGrey : 'initial')};
  border-radius: 50%;
  width: 1.7rem;
  height: 1.7rem;

  &:hover,
  &:focus {
    background: ${(p) => p.theme.VeryLightGrey};
  }

  .chevron {
    transition: transform 200ms;
    transition-timing-function: ${(p) => p.theme.timingFunction};
    transform: ${(p) => (p.isOpen ? 'rotate(270deg)' : 'rotate(90deg)')};
  }
`;

export const ListItemContent = styled.p<ThemeProps<'span'>>`
  white-space: nowrap;
  padding: ${(p) => p.theme.spacing.medium};
  margin: 0;
  cursor: default;
  text-align: left;

  ${({ isActive, theme }) => css`
    background: ${isActive && theme.VeryLightGrey};
    font-weight: ${isActive && 600};

    ${ItemWrapper}:hover &,
    ${ItemWrapper}[aria-selected='true'] & {
      background: ${({ theme }) => theme.Sand};
    }
  `}
`;

export const StyledTableHead = styled.thead<ThemeProps<'thead'>>`
  ${({ tableName }) =>
    (tableName === 'BrowseOpus' || tableName === 'TopLists') &&
    `tr {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }`}
  ${Header} {
    &:first-of-type {
      ${HeaderContent},
      ${HeaderButton} {
        margin-right: auto;
        ${({ tableName }) =>
          tableName === 'chart' ? 'white-space: normal;' : ''}
      }
    }
    ${({ tableName }) =>
      tableName === 'chart' &&
      `
      &:last-of-type {
        ${HeaderContent},
        ${HeaderButton} {
          display: block;
          white-space: normal;
          width: 7em;
          text-align: left;
        }
        div > div {
          margin-left: -2em;
        }
      }
      &:nth-last-child(2) {
        ${HeaderContent},
        ${HeaderButton} {
          display: block;
          white-space: normal;
          width: 6em;
          text-align: left;
          margin-right: -0.5em;
        }
      }
      `}
  }
`;

export const ListLabel = styled.span`
  ${definitionLabel};
  background: white;
  display: block;
  padding: ${(p) => p.theme.spacing.medium};
`;

export const SubCell = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Divider = styled.div`
  margin: 4px 8px 0;
  width: 1px;
  height: 9px;
  background-color: #8ea6be;
`;

export const MainLabel = styled.span<{
  withLeftMargin?: boolean;
  withDashedLine?: boolean;
}>`
  font-size: 16px;
  color: ${(p) => p.theme.DeepBlue};
  text-transform: none;
  font-weight: 600;
  letter-spacing: normal;
  margin-left: ${({ withLeftMargin }): any => withLeftMargin && '5px'};
  ${({ withDashedLine }) =>
    withDashedLine && `border-bottom: 1px dashed #8DA5BF;`}
`;

export const StyledTableRow = styled.tr<{
  alignBottom: boolean;
  isHeadSticky: boolean;
}>`
  vertical-align: ${({ alignBottom }): string =>
    alignBottom ? 'bottom' : 'inherit'};
  ${({ isHeadSticky }) => isHeadSticky && 'position: sticky; top: 0px;'}
`;
